import classnames from "classnames";
import { useEffect, useState } from "react";
import Moment from "react-moment";
import { connect } from "react-redux";
import { Button, Input } from "reactstrap";
import {
  addSystemSetting,
  deleteSystemSetting,
  editSystemSetting,
  getSystemSettings,
} from "../../../actions";
import { formValidation } from "../../../function";
import Datatable from "../../Datatable";
import Paginations from "../../Datatable/Paginations";
import Layouts from "../../Layouts";
import FormConfirmation from "../../Modals/FormConfirmation";
import SideModal from "../../Modals/SideModal";
import AddSystemSettingForm from "./AddSystemSettingForm.js";
import EditSystemSettingForm from "./EditSystemSettingForm.js";
import {
  formatSettingValueDisplay,
  normalizeSettingType,
  validateSettingValueForType,
} from "../../../constants/settingValueTypes";

const requiredField = {
  setting_key: ["required"],
  setting_type: ["required"],
  setting_value: ["required"],
};

const validateSystemSetting = (data) => {
  const errors = {};

  if (!data.setting_key?.trim()) {
    errors.setting_key = "Setting key is required.";
  } else if (data.setting_key.length > 50) {
    errors.setting_key = "Setting key cannot exceed 50 characters.";
  }

  if (!data.setting_type?.trim()) {
    errors.setting_type = "Value type is required.";
  }

  const valueError = validateSettingValueForType(
    data.setting_type,
    data.setting_value,
  );
  if (valueError) {
    errors.setting_value = valueError;
  }

  if (!data.setting_description?.trim()) {
    errors.setting_description = "Setting description is required.";
  } else if (data.setting_description.length > 500) {
    errors.setting_description =
      "Setting description cannot exceed 500 characters.";
  }

  return errors;
};

const flattenSettingErrors = (message) => {
  if (!message || typeof message !== "object" || Array.isArray(message)) {
    return { setting_value: String(message || "Request failed") };
  }
  const next = {};
  Object.keys(message).forEach((key) => {
    const value = message[key];
    next[key] = Array.isArray(value) ? value[0] : value;
  });
  return next;
};

const SystemSetting = (props) => {
  const [searchText, setSearchText] = useState(null);
  const [openAddForm, setOpenAddForm] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [systemSettingList, setSystemSettingList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addFormData, setAddFormData] = useState({
    setting_type: "STRING",
  });
  const [editFormData, setEditFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [, setFormType] = useState(null);
  const [formChanged, setFormChanged] = useState(false);
  const [formConfirm, setFormConfirm] = useState(false);
  const [, setDeleteConfirm] = useState(false);
  const [deleteSystemSettingData, setDeleteSystemSettingData] = useState(null);
  const { user } = props;

  useEffect(() => {
    const getSystemSettings = async () => {
      const res = await props.getSystemSettings(props.user.token);
      setSystemSettingList(res.data);
      setIsLoading(false);
    };
    getSystemSettings();
  }, [props]);

  const handleChange = (e) => {
    setSearchText(e.target.value);
    setPageActive(1);
  };

  const handlePopup = (e) => {
    if (formChanged) {
      setFormConfirm(true);
      return false;
    }
    setOpenAddForm(e);
    setErrors({});
    if (e) {
      setAddFormData({ setting_type: "STRING" });
    }
  };

  const formConfirmPopup = (confirm) => {
    if (confirm) {
      setFormChanged(false);
      setOpenAddForm(false);
      setOpenEditForm(false);
      setErrors({});
    }
    return setFormConfirm(false);
  };

  const handleEditPopup = () => {
    if (formChanged) {
      setFormConfirm(true);
      return false;
    }
    setOpenEditForm(false);
    setErrors({});
  };

  const handleFormChange = (e) => {
    setFormType("add");
    setFormChanged(true);
    setAddFormData({ ...addFormData, [e.target.name]: e.target.value });
  };

  const handleEditFormChange = (e) => {
    setFormType("edit");
    setFormChanged(true);

    return setEditFormData({
      ...editFormData,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const postData = {
      ...addFormData,
    };

    const validationErrors = validateSystemSetting(postData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    let validate = formValidation(requiredField, postData);

    if (validate.formIsValid) {
      const res = await props.addSystemSetting(props.user.token, postData);
      if (res.code >= 200 && res.code < 400) {
        let systemSettings = systemSettingList.concat(res.data);
        setSystemSettingList(systemSettings);
        setOpenAddForm(false);
        setErrors({});
        setFormChanged(false);
        setAddFormData({ setting_type: "STRING" });
      } else {
        setErrors(flattenSettingErrors(res.message));
      }
    } else {
      setErrors(validate.errors);
    }
  };

  const userUpdate = async (e) => {
    if (e) {
      e.preventDefault();
    }

    const postUpdateData = {
      id: editFormData.id,
      setting_key: editFormData.setting_key,
      setting_value: editFormData.setting_value,
      setting_description: editFormData.setting_description,
      setting_type: normalizeSettingType(editFormData.setting_type),
    };

    let validateField = { ...requiredField };

    const validationErrors = validateSystemSetting(postUpdateData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    let validate = formValidation(validateField, postUpdateData);
    if (validate.formIsValid) {
      apiCall(postUpdateData);
    } else {
      setErrors(validate.errors);
    }
  };

  const apiCall = async (apiData) => {
    const resSystemSetting = await props.editSystemSetting(
      props.user.token,
      apiData,
    );

    if (resSystemSetting.code >= 200 && resSystemSetting.code < 400) {
      let systemSetting = systemSettingList.map((item) => {
        if (resSystemSetting.data.id === item.id) {
          return (item = resSystemSetting.data);
        }
        return item;
      });
      setSystemSettingList(systemSetting);
      setOpenEditForm(false);
      setErrors({});
      setEditFormData({});
      setFormChanged(false);
    } else {
      setErrors(flattenSettingErrors(resSystemSetting.message));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const deleteConfirmPopup = (confirm) => {
    if (confirm) {
      const deleteSystemSetting = async () => {
        const res = await props.deleteSystemSetting(
          props.user.token,
          deleteSystemSettingData,
        );
        if (res.code >= 200 && res.code < 400) {
          let systemSettings = systemSettingList.filter(
            (item) => item.id !== deleteSystemSettingData.id,
          );
          setSystemSettingList(systemSettings);
          setOpenAddForm(false);
          setErrors({});
          setFormChanged(false);
          setDeleteConfirm(false);
          setDeleteSystemSettingData(null);
        } else {
          setErrors(res.message);
        }
      };
      deleteSystemSetting();
    } else {
      setDeleteConfirm(false);
      setDeleteSystemSettingData(null);
    }
  };

  const showSystemSetting = (systemSetting) => {
    setOpenEditForm(true);
    setEditFormData({
      ...systemSetting,
      setting_type: normalizeSettingType(systemSetting.setting_type),
    });
  };

  const column = [
    {
      fieldName: "setting_key",
      label: "Setting Key",
      sort: true,
      customRow: (row) => (
        <span className={classnames({ "opacity-50": false })}>
          {row.setting_key}
        </span>
      ),
    },
    {
      fieldName: "setting_value",
      label: "Setting Value",
      sort: true,
      customRow: (row) => (
        <span className={classnames({ "opacity-50": false })}>
          {formatSettingValueDisplay(row)}
        </span>
      ),
    },
    {
      fieldName: "setting_description",
      label: "Description",
      sort: true,
      customRow: (row) => (
        <span className={classnames({ "opacity-50": false })}>
          {row.setting_description}
        </span>
      ),
    },
    {
      fieldName: "created_at",
      label: "Added On",
      sort: true,
      customRow: (row) => (
        <span className={classnames({ "opacity-50": false })}>
          <Moment format="DD/MM/YYYY">{row.created_at}</Moment>
        </span>
      ),
    },
    {
      fieldName: "",
      label: "Actions",
      width: 80,
      align: "center",
      customRow: (row) =>
        user && user.user.role_id === 1 ? (
          <div className="d-flex justify-content-center">
            <Button
              size="sm"
              color="default"
              onClick={() => showSystemSetting(row)}
            >
              <span className="fa fa-pencil"></span>
            </Button>
          </div>
        ) : (
          <></>
        ),
    },
  ];
  const [showEntry] = useState(10);
  const [pageActive, setPageActive] = useState(1);
  const [totalRecords, setTotalRecords] = useState(systemSettingList.length);

  const handlePagination = (e) => {
    setPageActive(e);
  };

  const changeTableParams = (total, page) => {
    if (page) setPageActive(page);
    setTotalRecords(total);
  };

  return (
    <Layouts>
      <SideModal show={openAddForm} close={handlePopup}>
        <AddSystemSettingForm
          errors={errors}
          change={handleFormChange}
          fillData={addFormData}
          submit={onSubmit}
          close={handlePopup}
          requiredField={requiredField}
        />
      </SideModal>

      <SideModal show={openEditForm} close={handleEditPopup}>
        <EditSystemSettingForm
          errors={errors}
          change={handleEditFormChange}
          fillData={editFormData}
          submit={userUpdate}
          close={handleEditPopup}
          requiredField={requiredField}
          user={user}
        />
      </SideModal>

      <FormConfirmation
        show={formConfirm}
        toggle={handlePopup}
        confirm={formConfirmPopup}
      />

      <div className="p-20">
        <div className="card">
          <div className="cardHeader">
            <h3>
              System Settings
              <small className="d-block">
                Total - {systemSettingList.length}
              </small>
            </h3>
            <div className="searchBox">
              <Input
                type="text"
                placeholder="Search..."
                name="searchText"
                onChange={(e) => handleChange(e)}
              />
            </div>
            {user && user.user.role_id === 1 && (
              <Button className="linkBtn" onClick={() => handlePopup(true)}>
                <i className="fa fa-plus mr-2"></i>
                Add New System Setting
              </Button>
            )}
          </div>
          <Datatable
            data={systemSettingList}
            columns={column}
            pageActive={pageActive}
            showEntry={showEntry}
            searchText={searchText}
            changeTableParams={changeTableParams}
            loading={isLoading}
          />
        </div>
        {totalRecords > showEntry && (
          <Paginations
            totalRecords={totalRecords}
            showEntry={showEntry}
            pageActive={pageActive}
            click={handlePagination}
            prevClick={() => setPageActive(parseInt(pageActive) - 1)}
            nextClick={() => setPageActive(parseInt(pageActive) + 1)}
          />
        )}
      </div>
    </Layouts>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps, {
  getSystemSettings,
  addSystemSetting,
  editSystemSetting,
  deleteSystemSetting,
})(SystemSetting);
